import { toast } from "sonner";
import type { AssignableTask } from "@/schemas/time";

/**
 * A shared task link (Vinyl's "copy link", used for company-wide events): the Time page with Log
 * time open on the task. Only the task id is in it, never a person or an assignment, so whoever
 * opens it logs to their own assignment (or is told they have none).
 */
export function taskLinkUrl(origin: string, projectTaskId: string): string {
  const params = new URLSearchParams({ log: "open", task: projectTaskId });
  return `${origin}/dashboard/time?${params.toString()}`;
}

/** What a task link opens for the person: their one assignment, a choice of several, or none. */
export type TaskLinkResolution =
  | { kind: "one"; task: AssignableTask }
  | { kind: "several"; tasks: AssignableTask[] }
  | { kind: "none" };

/** The person's assignments to the linked task (the server already filtered to it). */
export function resolveTaskLink(items: AssignableTask[]): TaskLinkResolution {
  const seen = new Set<string>();
  const tasks = items.filter((t) => !seen.has(t.key) && seen.add(t.key));
  const [first] = tasks;
  if (!first) return { kind: "none" };
  return tasks.length === 1 ? { kind: "one", task: first } : { kind: "several", tasks };
}

export const INVALID_LINK_TEXT = "This task link isn't valid.";

export const NOT_ASSIGNED_TEXT =
  "You're not assigned to this task, so you can't log time to it. Ask your manager to add you.";

/** Copies the task's link and says so; a refused clipboard says that instead. */
export async function copyTaskLink(
  projectTaskId: string,
  clipboard: Pick<Clipboard, "writeText"> | undefined = globalThis.navigator?.clipboard,
  origin: string = globalThis.location?.origin ?? "",
): Promise<void> {
  try {
    if (!clipboard) throw new Error("No clipboard");
    await clipboard.writeText(taskLinkUrl(origin, projectTaskId));
    toast.success("Link copied", { id: "task-link" });
  } catch {
    toast.error("Couldn't copy the link", { id: "task-link" });
  }
}
